import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { CheckCircle, Rocket, Zap, Target, Users, ArrowRight, Star, Play, X } from 'lucide-react';

export const LaunchPulse = () => {
  const [timeLeft, setTimeLeft] = useState({
    days: 7,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  useEffect(() => {
    const deadline = new Date();
    deadline.setDate(deadline.getDate() + 7);
    
    const timer = setInterval(() => {
      const now = new Date().getTime();
      const distance = deadline - now;
      
      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);
      
      setTimeLeft({ days, hours, minutes, seconds });
      
      if (distance < 0) {
        clearInterval(timer);
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      }
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const features = [
    {
      icon: <Zap className="w-8 h-8" />,
      title: "Análise em 5 Minutos",
      description: "Descubra em 5 minutos se sua oferta vai converter ou fracassar com precisão científica"
    },
    {
      icon: <Target className="w-8 h-8" />,
      title: "IA Especializada",
      description: "Primeira IA brasileira treinada especificamente em lançamentos que geram 9 dígitos"
    },
    {
      icon: <Rocket className="w-8 h-8" />,
      title: "Estratégias Garantidas",
      description: "Garantimos estrutura e estratégias de lançamentos de 9 dígitos dos maiores players de marketing do mundo"
    }
  ];

  const benefits = [
    {
      icon: "🎯",
      title: "Clareza total da sua oferta",
      subtitle: "(sem achismo)"
    },
    {
      icon: "🚀",
      title: "Estratégia de lançamento completa",
      subtitle: "do início ao fim"
    },
    {
      icon: "⚡",
      title: "Site do seu produto/oferta",
      subtitle: "e sequências prontas muito mais rápido"
    },
    {
      icon: "✅",
      title: "Você sabe o próximo passo",
      subtitle: "e executa sem depender de equipe grande"
    }
  ];

  const testimonials = [
    {
      name: "Cliente Satisfeito",
      role: "Empreendedor Digital",
      content: "Em uma semana eu já tinha toda a estratégia do meu lançamento pronta e clareza para executar tudo!",
      avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=60&h=60&fit=crop&crop=face"
    },
    {
      name: "Marília Miranda", 
      role: "Mentora de Negócios",
      content: "Tive acesso ao playbook completo: conteúdos, copys de criativos, roteiros de vídeo, copy do vídeo de vendas e, principalmente, a criação da minha oferta. É uma mão na roda — não vivo sem o LaunchPulse!",
      avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=60&h=60&fit=crop&crop=face"
    },
    {
      name: "Léo Oliveira",
      role: "Fundador LaunchPulse", 
      content: "Montei toda minha estratégia de lançamento sem contratar estrategista, copy e social media. Economizei mais de R$ 30 mil só de equipe.",
      avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b27c?w=60&h=60&fit=crop&crop=face"
    }
  ];

  const faqData = [
    {
      question: "Funciona para quem?",
      answer: "Para quem quer lançar ou vender produto físico ou digital, em perpétuo ou lançamento tradicional."
    },
    {
      question: "Qual a diferença do Diamante para o Founder?",
      answer: "Diamante faz análises ilimitadas da oferta com relatório de melhorias. Founder entrega playbook completo, ativos prontos e mentoria a cada 15 dias com acesso anual."
    },
    {
      question: "Tem garantia?",
      answer: "Sim, garantia de 7 dias para cancelar."
    },
    {
      question: "Preciso ter experiência?",
      answer: "Não. Você segue as instruções e executa passo a passo."
    },
    {
      question: "Posso migrar de plano?",
      answer: "Sim. É possível migrar para o Founder enquanto a janela estiver aberta."
    }
  ];

  return (
    <div className="min-h-screen bg-black text-white font-mono overflow-x-hidden">
      {/* Floating Particles Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-cyan-400 rounded-full animate-pulse opacity-60"></div>
        <div className="absolute top-1/3 right-1/3 w-1 h-1 bg-cyan-300 rounded-full animate-pulse opacity-40 delay-300"></div>
        <div className="absolute bottom-1/4 left-1/3 w-1.5 h-1.5 bg-cyan-500 rounded-full animate-pulse opacity-50 delay-700"></div>
        <div className="absolute top-2/3 right-1/4 w-1 h-1 bg-cyan-400 rounded-full animate-pulse opacity-30 delay-1000"></div>
      </div>

      {/* Header */}
      <header className="fixed top-0 w-full z-50 backdrop-blur-sm bg-black/90 border-b border-white/25">
        <div className="max-w-7xl mx-auto px-4 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-cyan-400 flex items-center justify-center">
              <Rocket className="w-6 h-6 text-black" />
            </div>
            <span className="text-2xl font-bold text-cyan-400">LaunchPulse</span>
          </div>
          
          <nav className="hidden md:flex items-center gap-8">
            <a href="#como-funciona" className="text-gray-400 hover:text-white transition-colors duration-300">Como funciona</a>
            <a href="#planos" className="text-gray-400 hover:text-white transition-colors duration-300">Planos</a>
            <a href="#founder" className="text-gray-400 hover:text-white transition-colors duration-300">Founder</a>
            <a href="#depoimentos" className="text-gray-400 hover:text-white transition-colors duration-300">Depoimentos</a>
            <a href="#faq" className="text-gray-400 hover:text-white transition-colors duration-300">FAQ</a>
          </nav>

          <Button className="bg-cyan-400 text-black hover:bg-white transition-all duration-300 font-medium px-6">
            Quero minha Análise Grátis (5 min)
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-30"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1614728263952-84ea256f9679?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxyb2NrZXQlMjBsYXVuY2h8ZW58MHx8fHwxNzU3NTk3NTQ3fDA&ixlib=rb-4.1.0&q=85')`
          }}
        />
        
        {/* Glass morphism overlay */}
        <div className="absolute inset-0 backdrop-blur-[2px] bg-black/50" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 text-center">
          {/* Main Headline */}
          <div className="space-y-6">
            <h1 className="text-4xl lg:text-6xl font-black leading-tight">
              A Primeira IA Brasileira que
              <span className="block text-cyan-400 mt-2">Cria sua Oferta Ultra Matadora</span>
              <span className="block mt-2">e a estratégia completa do seu lançamento</span>
              <span className="block text-yellow-400 mt-2">(com potencial de 9 dígitos)</span>
            </h1>
          </div>

          {/* Sub-headline */}
          <p className="text-xl lg:text-2xl text-gray-300 max-w-5xl mx-auto leading-relaxed mt-8">
            Pare de bater cabeça. Em poucos cliques você ganha clareza de oferta e recebe tudo que precisa para executar sem travar. Mesmo que você nunca tenha vendido nada na internet, nossa IA transforma sua oferta medíocre em uma <strong className="text-cyan-400">Oferta Matadora</strong> para Produtos Físicos ou Produtos Digitais!
          </p>

          {/* Social Proof */}
          <div className="flex flex-wrap justify-center items-center gap-8 py-8">
            <div className="text-center">
              <div className="text-3xl lg:text-4xl font-black text-cyan-400">Mesma Estratégia</div>
              <div className="text-sm text-gray-400">dos Maiores Players</div>
            </div>
            <div className="text-center">
              <div className="text-3xl lg:text-4xl font-black text-purple-400">9 Dígitos</div>
              <div className="text-sm text-gray-400">100M+ Dólares</div>
            </div>
            <div className="text-center">
              <div className="text-3xl lg:text-4xl font-black text-yellow-400">5 Minutos</div>
              <div className="text-sm text-gray-400">Análise Completa</div>
            </div>
          </div>

          {/* CTA */}
          <div className="space-y-4">
            <Button className="bg-cyan-400 text-black hover:bg-white text-xl font-bold px-12 py-6 transition-all duration-300 hover:scale-105 w-full lg:w-auto">
              🚀 Quero minha Análise Gratuita Agora
            </Button>
            <p className="text-sm text-gray-400 flex flex-wrap justify-center items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-400" />
              Resultado em 5 minutos
              <CheckCircle className="w-4 h-4 text-green-400 ml-4" />
              Relatório de Análise da Oferta
              <CheckCircle className="w-4 h-4 text-green-400 ml-4" />
              Score da Oferta e pontos de melhoria
              <CheckCircle className="w-4 h-4 text-green-400 ml-4" />
              100% Gratuita
            </p>
          </div>
        </div>
      </section>

      {/* Pare Tudo */}
      <section className="py-16 bg-gradient-to-r from-red-900/20 to-red-700/20 border-y border-red-500/30">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h2 className="text-4xl lg:text-6xl font-black text-red-400 mb-8">
            ⚠️ PARE TUDO QUE VOCÊ ESTÁ FAZENDO ⚠️
          </h2>
          <p className="text-xl lg:text-2xl text-gray-300 max-w-4xl mx-auto">
            Se você está trabalhando 14 horas por dia, refém do operacional, perdendo vendas porque não consegue criar ofertas que convertem...
          </p>
        </div>
      </section>

      {/* Apresentando o LaunchPulse */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-6xl font-black mb-6">
              Apresentando o <span className="text-cyan-400">LaunchPulse</span>
            </h2>
            <p className="text-xl lg:text-2xl text-gray-300 max-w-4xl mx-auto">
              O único sistema que te ensina a usar IA em TODAS as fases do lançamento - da concepção ao debriefing
            </p>
          </div>

          {/* S.M.A.R.T System */}
          <Card className="bg-gray-900/60 backdrop-blur-md border-white/25 p-8 lg:p-12 mb-16 max-w-5xl mx-auto">
            <h3 className="text-3xl lg:text-4xl font-black text-center mb-12 text-cyan-400">Sistema S.M.A.R.T.™</h3>
            <div className="grid md:grid-cols-5 gap-8">
              {[
                { letter: 'S', title: 'Sistema', desc: 'Metodologia científica comprovada', color: 'bg-blue-500' },
                { letter: 'M', title: 'Magnético', desc: 'Ofertas irresistíveis que atraem clientes', color: 'bg-purple-500' },
                { letter: 'A', title: 'Avaliação', desc: 'Análise precisa de 1-10', color: 'bg-green-500' },
                { letter: 'R', title: 'Ranking', desc: 'Classificação científica', color: 'bg-red-500' },
                { letter: 'T', title: 'Tempo Record', desc: 'Estrutura completa de um lançamento milionário em curtíssimo prazo!', color: 'bg-cyan-500' }
              ].map((item, index) => (
                <div key={index} className="text-center group hover:scale-105 transition-all duration-300">
                  <div className={`w-20 h-20 ${item.color} flex items-center justify-center mx-auto mb-4 group-hover:shadow-lg group-hover:shadow-cyan-400/20`}>
                    <span className="text-white font-black text-2xl">{item.letter}</span>
                  </div>
                  <h4 className="font-bold text-lg mb-2 text-white">{item.title}</h4>
                  <p className="text-sm text-gray-400">{item.desc}</p>
                </div>
              ))}
            </div>
          </Card>

          {/* Main Features */}
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {features.map((feature, index) => (
              <Card key={index} className="bg-gray-900/60 backdrop-blur-md border-white/25 p-8 text-center group hover:scale-105 hover:border-cyan-400/50 transition-all duration-300">
                <div className="w-20 h-20 bg-cyan-400 flex items-center justify-center mx-auto mb-6 group-hover:bg-white transition-colors">
                  <div className="text-black">
                    {feature.icon}
                  </div>
                </div>
                <h3 className="text-xl lg:text-2xl font-bold mb-4">{feature.title}</h3>
                <p className="text-gray-400 leading-relaxed">{feature.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {benefits.map((benefit, index) => (
              <Card key={index} className="bg-gray-900/60 backdrop-blur-md border-white/25 p-6 text-center hover:scale-105 hover:border-cyan-400/50 transition-all duration-300">
                <div className="w-16 h-16 bg-cyan-400/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">{benefit.icon}</span>
                </div>
                <h3 className="text-lg font-bold mb-3 text-cyan-400">{benefit.title}</h3>
                <p className="text-gray-400 text-sm">{benefit.subtitle}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="como-funciona" className="py-20 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-6xl font-black mb-6">Como Funciona em 3 Passos Simples</h2>
            <p className="text-xl lg:text-2xl text-gray-300">Da oferta medíocre ao lançamento de 9 dígitos</p>
          </div>

          <div className="max-w-5xl mx-auto space-y-16">
            {[
              {
                step: '1',
                title: 'Insira sua oferta',
                description: 'Formulário rápido (≤ 2 min)',
                image: 'https://images.unsplash.com/photo-1541185933-ef5d8ed016c2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwzfHxyb2NrZXQlMjBsYXVuY2h8ZW58MHx8fHwxNzU3NTk3NTQ3fDA&ixlib=rb-4.1.0&q=85'
              },
              {
                step: '2',
                title: 'Analisamos sua oferta',
                description: 'Você recebe um diagnóstico em 5 minutos',
                image: 'https://images.unsplash.com/photo-1457365050282-c53d772ef8b2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHw0fHxyb2NrZXQlMjBsYXVuY2h8ZW58MHx8fHwxNzU3NTk3NTQ3fDA&ixlib=rb-4.1.0&q=85'
              },
              {
                step: '3',
                title: 'Aplique a estratégia',
                description: 'Recomendações claras; no Founder entregamos playbook completo e ativos prontos',
                image: 'https://images.pexels.com/photos/73871/rocket-launch-rocket-take-off-nasa-73871.jpeg'
              }
            ].map((item, index) => (
              <div key={index} className={`flex flex-col ${index % 2 === 1 ? 'lg:flex-row-reverse' : 'lg:flex-row'} items-center gap-12`}>
                <div className="lg:w-1/2 space-y-6">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-cyan-400 text-black flex items-center justify-center text-2xl font-black">
                      {item.step}
                    </div>
                    <h3 className="text-2xl lg:text-3xl font-black">{item.title}</h3>
                  </div>
                  <p className="text-gray-300 text-lg leading-relaxed">{item.description}</p>
                </div>
                <div className="lg:w-1/2">
                  <Card className="bg-gray-900/60 backdrop-blur-md border-white/25 p-2 hover:scale-105 transition-all duration-300">
                    <div 
                      className="h-64 lg:h-80 bg-cover bg-center flex items-center justify-center"
                      style={{ backgroundImage: `url('${item.image}')` }}
                    >
                      <div className="absolute inset-0 bg-black/40 backdrop-blur-[1px]" />
                      <div className="relative z-10 text-center">
                        <div className="text-cyan-400 text-6xl mb-4">📝</div>
                        <p className="text-white font-bold">Passo {item.step} em Ação</p>
                      </div>
                    </div>
                  </Card>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="planos" className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-6xl font-black mb-6">Escolha Seu Plano</h2>
            <p className="text-xl lg:text-2xl text-gray-300">Da análise gratuita ao império de 9 dígitos</p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Diagnóstico Elite */}
            <Card className="bg-gray-900/60 backdrop-blur-md border-white/25 p-8">
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold mb-4">DIAGNÓSTICO ELITE</h3>
                <div className="text-4xl font-black text-cyan-400 mb-2">GRÁTIS</div>
                <p className="text-gray-400 mb-2">Para sempre</p>
                <p className="text-sm text-cyan-400/80">Acesso aos segredos da elite mundial</p>
              </div>

              <ul className="space-y-4 mb-8">
                {[
                  '1 Análise da Oferta por mês (entrega em 5 minutos)',
                  'Relatório básico (5 páginas)',
                  '3 recomendações práticas'
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <span className="text-gray-300">{item}</span>
                  </li>
                ))}
              </ul>

              <Button className="w-full bg-gray-600 text-white hover:bg-gray-500 transition-all duration-300">
                Começar Grátis
              </Button>
            </Card>

            {/* Oferta Diamante */}
            <Card className="bg-gray-900/60 backdrop-blur-md border-cyan-400 p-8 relative scale-105 lg:scale-110">
              <Badge className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-cyan-400 text-black px-4 py-2 font-bold">
                MAIS POPULAR
              </Badge>

              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold mb-4">OFERTA DIAMANTE</h3>
                <div className="text-4xl font-black text-cyan-400 mb-2">R$ 997</div>
                <p className="text-gray-400 mb-2"><strong>/mês</strong></p>
                <p className="text-sm text-cyan-400/80">Lapidada à perfeição pelos mestres</p>
              </div>

              <ul className="space-y-4 mb-8">
                {[
                  'Análises de oferta ilimitadas',
                  'Relatório de Pontos de Melhoria (profundo)',
                  'Suporte prioritário'
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <span className="text-gray-300">{item}</span>
                  </li>
                ))}
                <li className="flex items-center gap-3">
                  <X className="w-5 h-5 text-red-400 flex-shrink-0" />
                  <span className="text-gray-300">Não inclui o passo a passo do playbook de lançamento</span>
                </li>
              </ul>

              <Button className="w-full bg-cyan-400 text-black hover:bg-white transition-all duration-300 hover:scale-105">
                Assinar Diamante
              </Button>
            </Card>

            {/* Império de 9 Dígitos */}
            <Card className="bg-gradient-to-br from-purple-900/60 to-blue-900/60 backdrop-blur-md border-yellow-400 p-8 relative">
              <Badge className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-yellow-400 text-black px-4 py-2 font-bold">
                FOUNDER
              </Badge>

              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold mb-4 text-white">IMPÉRIO DE 9 DÍGITOS</h3>
                <div className="text-4xl font-black text-yellow-400 mb-2">R$ 5.000</div>
                <p className="text-blue-200 mb-2"><strong>/ano ou 12× R$ 497</strong></p>
                <p className="text-sm text-yellow-300/80">Construa seu império de 9 dígitos com lançamentos automatizados</p>
              </div>

              {/* Countdown */}
              <Card className="bg-red-600/20 border border-red-500 p-4 mb-6 text-center">
                <div className="text-red-400 font-bold mb-2">⏰ Oferta encerra em:</div>
                <div className="text-2xl font-bold text-red-300">
                  {timeLeft.days}d {timeLeft.hours}h {timeLeft.minutes}m {timeLeft.seconds}s
                </div>
              </Card>

              <ul className="space-y-4 mb-8">
                {[
                  'Tudo do Diamante +',
                  'Playbook completo do lançamento (7 etapas)',
                  'Site do seu produto/oferta v1 + 5 e-mails + 10 hooks',
                  'Mentoria em grupo a cada 15 dias',
                  'Comunidade e acesso anual'
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-yellow-300 flex-shrink-0" />
                    <span className="text-blue-100">{item}</span>
                  </li>
                ))}
              </ul>

              <Button className="w-full bg-yellow-400 text-black hover:bg-yellow-300 transition-all duration-300 hover:scale-105">
                Quero ser Founder
              </Button>
            </Card>
          </div>
        </div>
      </section>

      {/* Chega de tentativa e erro */}
      <section className="py-20 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h2 className="text-4xl lg:text-6xl font-black mb-8 text-cyan-400">Chega de tentativa e erro</h2>
          <div className="max-w-4xl mx-auto space-y-6">
            <p className="text-xl lg:text-2xl text-gray-300">Você terá uma oferta forte, uma estratégia clara e materiais prontos para começar.</p>
            <p className="text-xl lg:text-2xl text-cyan-400 font-semibold">Execute em dias, não em semanas.</p>
            <p className="text-xl lg:text-2xl text-gray-300">Foque no que vende: mensagem, tráfego e conversão.</p>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="depoimentos" className="py-20 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-6xl font-black mb-6">O Que Nossos Clientes Estão Dizendo</h2>
            <p className="text-xl lg:text-2xl text-gray-300">Resultados reais de pessoas reais</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="bg-gray-900/60 backdrop-blur-md border-white/25 p-8 hover:scale-105 hover:border-cyan-400/50 transition-all duration-300">
                <p className="text-gray-300 mb-6 leading-relaxed italic">"{testimonial.content}"</p>
                <div className="flex items-center gap-4">
                  <img 
                    src={testimonial.avatar} 
                    alt={testimonial.name}
                    className="w-16 h-16 rounded-full object-cover"
                  />
                  <div>
                    <h4 className="font-bold text-lg text-cyan-400">{testimonial.name}</h4>
                    <p className="text-sm text-gray-400">{testimonial.role}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-20 bg-black">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-6xl font-black mb-6">Perguntas Frequentes</h2>
            <p className="text-xl lg:text-2xl text-gray-300">Tire todas suas dúvidas</p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqData.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="bg-gray-900/60 backdrop-blur-md border-white/25 px-6">
                <AccordionTrigger className="text-left text-lg font-bold hover:text-cyan-400 transition-colors">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-300 leading-relaxed pt-4">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-cyan-900 to-blue-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url('https://images.unsplash.com/photo-1517976487492-5750f3195933?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwyfHxyb2NrZXQlMjBsYXVuY2h8ZW58MHx8fHwxNzU3NTk3NTQ3fDA&ixlib=rb-4.1.0&q=85')`
            }}
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 text-center">
          <h2 className="text-4xl lg:text-6xl font-black mb-8">Pronto Para Construir Seu Império?</h2>
          <p className="text-xl lg:text-2xl mb-12 max-w-4xl mx-auto text-cyan-100">
            Junte-se aos empreendedores que já descobriram o poder das ofertas ultra matadoras
          </p>

          <div className="flex flex-wrap justify-center gap-4 mb-8">
            <Button className="bg-cyan-400 text-black hover:bg-cyan-300 text-xl font-bold px-12 py-6 transition-all duration-300 hover:scale-105">
              🚀 Quero minha Análise Grátis (5 min)
            </Button>
            <Button className="bg-purple-600 text-white hover:bg-purple-500 text-xl font-bold px-12 py-6 transition-all duration-300 hover:scale-105">
              💎 Assinar Diamante
            </Button>
            <Button className="bg-yellow-400 text-black hover:bg-yellow-300 text-xl font-bold px-12 py-6 transition-all duration-300 hover:scale-105">
              👑 Quero ser Founder
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black py-16 border-t border-white/25">
        <div className="max-w-7xl mx-auto px-4">
          {/* Disclaimer Facebook */}
          <Card className="bg-gray-800/50 p-6 mb-8 text-sm text-gray-400">
            <p><strong>Disclaimer:</strong> Este site não é afiliado ao Facebook™ nem a nenhuma entidade do Facebook™. Depois que você sair do Facebook™, a responsabilidade não é deles e sim do nosso site. Fazemos todos os esforços para indicar e explicar claramente nossos produtos e usá-los para fins de exemplo. Não garantimos resultados.</p>
          </Card>

          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-cyan-400 flex items-center justify-center">
                  <Rocket className="w-5 h-5 text-black" />
                </div>
                <span className="text-xl font-bold text-cyan-400">LaunchPulse</span>
              </div>
              <p className="text-gray-400">A primeira IA brasileira especializada em ofertas ultra matadoras.</p>
            </div>

            <div>
              <h3 className="font-bold mb-6 text-white">Produto</h3>
              <ul className="space-y-3 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">Diagnóstico Elite</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Oferta Diamante</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Império de 9 Dígitos</a></li>
              </ul>
            </div>

            <div>
              <h3 className="font-bold mb-6 text-white">Suporte</h3>
              <ul className="space-y-3 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">Central de Ajuda</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Contato</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Comunidade</a></li>
              </ul>
            </div>

            <div>
              <h3 className="font-bold mb-6 text-white">Legal</h3>
              <ul className="space-y-3 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">Política de Privacidade</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Termos de Uso</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-white/25 pt-8 text-center text-gray-400">
            <p>LaunchPulse — todos os direitos reservados a Léo Oliveira — <a href="https://leomkt.com.br" className="text-cyan-400 hover:text-cyan-300 transition-colors">leomkt.com.br</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
};